/**
 * Reporte de errores de cliente a Supabase (tabla `client_errors`).
 *
 * Objetivo: dejar de perder los errores en la consola del navegador del
 * usuario. Cada error se inserta (fire-and-forget) y un webhook manda un aviso
 * inmediato por email (ver edge function `notify-error`).
 *
 * Principios:
 *  - NUNCA romper la UX: todo es best-effort y se traga sus propios fallos.
 *  - No spamear: throttle en cliente por firma + tope por sesión.
 *  - No registrar en demo (RLS lo bloquearía y no aporta).
 *  - Acotar tamaños (mensaje/stack/context) para no inflar la BD.
 */
import type { Json } from '@/shared/lib/database.types';
import { getIsDemoUser } from '@/shared/lib/demoGuard';

type ErrorSource =
  | 'window.onerror'
  | 'unhandledrejection'
  | 'boundary'
  | 'mutation'
  | 'query'
  | 'manual';

interface ReportInput {
  source: ErrorSource;
  message: string;
  stack?: string | null;
  context?: Record<string, unknown>;
  level?: 'warn' | 'error' | 'fatal';
}

const MAX_MESSAGE = 2000;
const MAX_STACK = 8000;
const MAX_CONTEXT_CHARS = 4000;
const THROTTLE_MS = 60_000; // misma firma: máx. 1 vez/min por cliente
const SESSION_CAP = 50; // tope duro de inserts por carga de página

const lastSentBySignature = new Map<string, number>();
let sessionCount = 0;

/** Hash corto y estable (djb2) para la firma de deduplicación. */
function hash(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

function firstStackFrame(stack?: string | null): string {
  if (!stack) return '';
  const lines = stack.split('\n').map((l) => l.trim());
  return lines.find((l) => l.startsWith('at ')) ?? lines[1] ?? '';
}

function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max) + '…' : s;
}

export function computeSignature(source: string, message: string, stack?: string | null): string {
  // Normaliza el mensaje para agrupar (quita UUIDs/números que varían).
  const norm = message
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '<id>')
    .replace(/\d+/g, '<n>');
  return hash(`${source}|${norm}|${firstStackFrame(stack)}`);
}

/**
 * Reporta un error. Fire-and-forget: no lanza, no bloquea, no espera.
 */
export function reportClientError(input: ReportInput): void {
  try {
    if (getIsDemoUser()) return;
    if (sessionCount >= SESSION_CAP) return;
    const message = truncate(String(input.message || 'Unknown error'), MAX_MESSAGE);
    const signature = computeSignature(input.source, message, input.stack);

    const now = Date.now();
    const last = lastSentBySignature.get(signature) ?? 0;
    if (now - last < THROTTLE_MS) return;
    lastSentBySignature.set(signature, now);
    sessionCount++;

    let context = input.context ?? {};
    try {
      if (JSON.stringify(context).length > MAX_CONTEXT_CHARS) {
        context = { note: 'context truncated' };
      }
    } catch {
      context = { note: 'context not serializable' };
    }

    const row = {
      level: input.level ?? 'error',
      source: input.source,
      message,
      stack: input.stack ? truncate(input.stack, MAX_STACK) : null,
      // Ya validado como serializable arriba; `Json` es el tipo de la columna.
      context: context as Json,
      url: typeof location !== 'undefined' ? location.href : null,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      app_version: import.meta.env.MODE ?? null,
      // Se manda explicitamente aunque la columna tenga `DEFAULT 'web'` (#54).
      // El valor por omision existe para que esta migracion no rompiera los
      // clientes ya desplegados, no para que nadie se apoye en el: un cliente
      // que no lo mande queda apuntado como web, y ese es justo el fallo
      // callado que la columna venia a evitar.
      platform: 'web' as const,
      signature,
    };

    // Fire-and-forget: import perezoso del cliente Supabase (no acoplar la carga
    // del módulo a la validación de env) + tragamos cualquier fallo.
    void import('@/shared/lib/supabase')
      .then(({ supabase }) => supabase.from('client_errors').insert(row))
      .then(() => undefined, () => undefined);
  } catch {
    /* nunca propagar desde el reporter */
  }
}

/**
 * ¿Es un error del que el navegador **no puede** contarnos nada?
 *
 * Cuando falla un script servido desde otro origen sin cabeceras CORS, la
 * política de mismo origen impide que la página vea el detalle: `window.onerror`
 * llega con el mensaje literal «Script error.», sin objeto de error, sin fichero
 * y con línea 0. No es que el error sea pequeño; es que el navegador se niega a
 * describirlo.
 *
 * Guardarlos no informa de nada y sí hace daño: el 2026-08-28 entraron cinco en
 * tres minutos desde `/privacy` y `/terms` —el beacon de Cloudflare, bloqueado
 * por algún navegador o extensión—, cinco filas idénticas y cinco intentos de
 * correo que no decían nada. Y esa tabla acaba de pasar a ser la vía por la que
 * también avisa el cliente nativo (#54): el ruido ahí cuesta más que antes.
 *
 * **La comprobación es estructural, no de texto.** Podría bastar con comparar el
 * mensaje contra «Script error.», pero eso depende de cómo lo escriba cada
 * navegador. Lo que se mira es si queda **algo** con lo que empezar: un objeto de
 * error —que trae la traza—, un fichero o una línea. Si hay cualquiera de las
 * tres, se manda; sólo se descarta cuando no hay ninguna, que es exactamente el
 * caso en que no habría nada que investigar.
 */
function esOpaco(e: ErrorEvent): boolean {
  const sinError = !(e.error instanceof Error);
  const sinSitio = !e.filename && !e.lineno && !e.colno;
  return sinError && sinSitio;
}

let installed = false;

/** Registra los handlers globales de errores no capturados. Idempotente. */
export function installGlobalErrorHandlers(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (e: ErrorEvent) => {
    if (esOpaco(e)) return;
    reportClientError({
      source: 'window.onerror',
      message: e.message || (e.error instanceof Error ? e.error.message : 'window error'),
      stack: e.error instanceof Error ? (e.error.stack ?? null) : null,
      context: { filename: e.filename, lineno: e.lineno, colno: e.colno },
    });
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason = e.reason;
    reportClientError({
      source: 'unhandledrejection',
      message: reason instanceof Error ? reason.message : String(reason),
      stack: reason instanceof Error ? (reason.stack ?? null) : null,
    });
  });
}
